import { LoadingButton } from "@mui/lab";
import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { CampaignsContext } from "contexts/Campaigns";
import { DAYS_OF_THE_WEEK, NewCampaignContext } from "contexts/NewCampaign";
import { SavedBrandCollectionsContext } from "contexts/SavedBrandCollections";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BentoBrand,
  CampaignStatus,
  SavedBrandStatus,
} from "schemas/dashboard";

import { ON_BEHALF_OF_TOKEN } from "utils/localStorage";

import CampaignBrandCard from "../BrandSearch/Brands/CampaignBrandCard";

export default function PreviewCampaign() {
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const {
    templates,
    startDate,
    endDate,
    startTime,
    endTime,
    selectedDays,
    timezone,
    previewNewCampaign,
    setPreviewBrands,
    previewBrands,
    setPreviewPage,
    createNewCampaign,
    previewCount,
    setPreviewCount,
    status,
  } = useContext(NewCampaignContext);
  const { fetchSavedBrandsForCollection } = useContext(
    SavedBrandCollectionsContext,
  );
  const { setOpenIntegrationDialog } = useContext(UserIntegrationsContext);
  const { addCampaign } = useContext(CampaignsContext);
  const navigate = useNavigate();
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [createLoading, setCreateLoading] = useState<boolean>(false);

  const selectedFollowups = templates?.slice(1, templates?.length);
  const isAdmin = Boolean(sessionStorage.getItem(ON_BEHALF_OF_TOKEN));

  const fetchMoreBrands = async () => {
    try {
      setLoadingMore(true);
      const res = await previewNewCampaign();

      const jsonMessage = await res.json();

      if (!res.ok) {
        setAlert(jsonMessage.message, "error");
        return;
      }
      setPreviewBrands((prev) => [
        ...prev,
        ...jsonMessage.previewedBentoBrands,
      ]);
      setPreviewCount(jsonMessage.totalCount);
      setPreviewPage((prev) => prev + 1);
    } catch (error) {
      setAlert(
        "Something went wrong, please contact hello@onbento.com for support or try again",
        "error",
      );
    } finally {
      setLoadingMore(false);
    }
  };

  const createCampaign = async () => {
    setCreateLoading(true);
    try {
      const campaign = await createNewCampaign();
      if (!campaign) {
        return;
      }

      if (previewCount) {
        campaign.campaignBrandCount = previewCount;
      }
      addCampaign(campaign);

      if (campaign?.savedBrandCollectionId || campaign?.isAllSavedCollection) {
        fetchSavedBrandsForCollection(
          campaign?.savedBrandCollectionId || 0,
          SavedBrandStatus.sent,
          true,
        );
        fetchSavedBrandsForCollection(
          campaign?.savedBrandCollectionId || 0,
          SavedBrandStatus.unsent,
          true,
        );
      }

      navigate("../..");
    } catch (error) {
      if (error?.message?.includes("Google integration")) {
        setOpenIntegrationDialog(true);
      } else {
        setErrorAlert(error);
      }
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Grid container alignItems="center" rowGap={2}>
          <Grid item xs={12}>
            <Typography variant="h6">Confirm Campaign</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontSize: 15, color: grey[700] }}>
              Campaign Brands ({previewCount} selected)
            </Typography>
          </Grid>
          <Grid item container xs={12}>
            <Grid item container xs={12} lg={8} gap={1}>
              {previewBrands.map((brand: BentoBrand) => (
                <Grid item xs={12} key={brand.id}>
                  <CampaignBrandCard brand={brand} />
                </Grid>
              ))}
              {previewCount && previewBrands.length < previewCount && (
                <LoadingButton
                  fullWidth
                  variant="contained"
                  sx={{
                    color: grey[800],
                    backgroundColor: grey[200],
                  }}
                  color="inherit"
                  disableElevation
                  size="small"
                  onClick={() => {
                    fetchMoreBrands();
                  }}
                  loading={loadingMore}
                >
                  Load More
                </LoadingButton>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontSize: 15, color: grey[700] }}>
              Campaign Details
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography>Outreach Template</Typography>
          </Grid>
          <Grid item xs={6} sm={8}>
            {templates[0].name}
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography>Campaign Dates</Typography>
          </Grid>
          <Grid item xs={6} sm={8}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>{startDate?.format("MMMM Do, YYYY")}</Grid>
              <Grid item>
                <Typography>to</Typography>
              </Grid>
              <Grid item>{endDate?.format("MMMM Do, YYYY")}</Grid>
            </Grid>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography>Sending Time</Typography>
          </Grid>
          <Grid item xs={6} sm={8}>
            <Grid container alignItems="center" xs={12} spacing={1}>
              <Grid item>{startTime?.format("h:mm A")}</Grid>
              <Grid item>
                <Typography>to</Typography>
              </Grid>
              <Grid item>{endTime?.format("h:mm A")}</Grid>
              <Grid item>{timezone}</Grid>
            </Grid>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography sx={{ mt: 0.75 }}>Days of the Week</Typography>
          </Grid>
          <Grid item xs={6} sm={8}>
            <ToggleButtonGroup value={selectedDays} size="small">
              {DAYS_OF_THE_WEEK.map((day) => (
                <ToggleButton key={day[0]} value={day[0]} disabled>
                  {day[1]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography sx={{ mt: selectedFollowups.length > 0 ? 0.75 : 0.5 }}>
              Follow-up Templates
            </Typography>
          </Grid>
          <Grid item xs={6} sm={8}>
            {selectedFollowups?.map((followup, index) => (
              <Grid container sx={{ mb: 1 }} key={index}>
                <Grid container alignItems="center">
                  {followup.name}
                </Grid>
                <Grid container>
                  <Typography
                    variant="caption"
                    sx={{ fontStyle: "italic", fontSize: 10 }}
                    color="textSecondary"
                  >
                    Sent 4 business days after previous email
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Grid>
          {isAdmin && (
            <>
              <Grid item xs={6} sm={4}>
                <Typography>Requires User's Approval</Typography>
              </Grid>
              <Grid item xs={6} sm={8}>
                <Typography>
                  {status === CampaignStatus.pending_approval
                    ? "Requires approval from user before campaign begins"
                    : "No approval required"}
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          onClick={() => {
            navigate("../details");
          }}
          disableElevation
        >
          Back
        </Button>
        <LoadingButton
          variant="contained"
          disableElevation
          loading={createLoading}
          onClick={createCampaign}
        >
          Create
        </LoadingButton>
      </DialogActions>
    </>
  );
}
